import { Check, Loader2, TriangleAlert, X } from "lucide-react";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { IngestionProgress } from "@/lib/ingestion-progress";
import { cn } from "@/lib/utils";

interface IngestionStatusCellProps {
  readonly progress: IngestionProgress;
}

/**
 * Products-table status cell as a quiet badge family: soft violet "x/n" with a
 * pulsing dot while the pipeline runs (stage list in the tooltip), emerald
 * check when complete, red when failed.
 */
export function IngestionStatusCell({ progress }: IngestionStatusCellProps) {
  if (!progress.isTracked) {
    return (
      <span className="inline-flex items-center rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
        Not tracked
      </span>
    );
  }

  if (progress.isFailed) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-md bg-red-50 px-1.5 py-0.5 text-[11px] font-medium text-red-700 dark:bg-red-950/40 dark:text-red-300"
        title={progress.error ? `Processing failed: ${progress.error.code}` : "Processing failed"}
      >
        <X className="h-3 w-3" />
        Failed
      </span>
    );
  }

  if (progress.isPartial) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
        title={progress.error ? `Processing partially completed: ${progress.error.code}` : "Processing partially completed"}
      >
        <TriangleAlert className="h-3 w-3" />
        Partial
      </span>
    );
  }

  if (progress.isComplete) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
        title={`Completed · ${progress.totalCount}/${progress.totalCount} stages`}
      >
        <Check className="h-3 w-3" />
        {progress.totalCount}/{progress.totalCount}
      </span>
    );
  }

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex cursor-default items-center gap-1.5 rounded-md bg-violet-50 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-violet-700 dark:bg-violet-950/40 dark:text-violet-300">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-violet-500" />
            {progress.completedCount}/{progress.totalCount}
          </span>
        </TooltipTrigger>
        <TooltipContent side="left">
          <div className="space-y-1.5">
            <p className="text-xs font-medium">
              {progress.currentStage?.label ?? "Processing"} · {progress.completedCount}/
              {progress.totalCount} stages
            </p>
            <ul className="space-y-1">
              {progress.stages.map((stage) => (
                <li key={stage.key} className="flex items-center gap-2 text-xs">
                  {stage.done ? (
                    <Check className="h-3 w-3 text-emerald-500" />
                  ) : stage.key === progress.currentStage?.key ? (
                    <Loader2 className="h-3 w-3 animate-spin text-violet-500" />
                  ) : (
                    <span className="mx-[3px] h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
                  )}
                  <span className={cn(!stage.done && "text-muted-foreground")}>{stage.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
